import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { pageComponent } from '../../higher-order-components/pageComponent'
import { projectClosingStatusGridOptions } from './gridOptions'
import ProjectClosingStatusHeader from './Header'
import { useProjectClosingStatusData } from './hooks/projectClosingStatusData'
import { usePageState } from './hooks/pageState'
import { useSearchCondition } from './hooks/useSearchCondition'
import DateVO from '../../../vo/DateVO'
import { fetchProjectClosingStatusCount } from './projectClosingStatus'
import {
  ColDef,
  ColumnState,
  FilterChangedEvent,
  GridApi,
  SortChangedEvent,
} from 'ag-grid-community'
import { SortedColumnState } from '../../model/bulkSheetColumnSortState'
import {
  useBulkSheetState,
  RestoreType,
} from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import SavedUIStateDialog from '../../components/dialogs/SavedUIStateDialog'
import MultiSelectDialog from '../../components/dialogs/MultiSelectDialog'
import { useDialog } from './hooks/dialog'
import { useExcel } from './hooks/excel'
import { intl } from '../../../i18n'
import _ from 'lodash'

type Props = PageProps

const ProjectClosingStatus = (props: Props) => {
  const functionUuid = props.uuid
  const gridOptions = useMemo(() => {
    return projectClosingStatusGridOptions()
  }, [])
  const { data, fetchRecords } = useProjectClosingStatusData()
  const ref = useRef<HTMLDivElement>(null)
  const pageState = usePageState(functionUuid)
  const [projectCount, setProjectCount] = useState<number>(0)
  const { startDate, endDate } = pageState.displayDateTerm
  const { searchCondition, onChange: onSearchConditionChange } =
    useSearchCondition()
  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )
  const gridApiRef = useRef<GridApi>()
  const [isGridReady, setIsGridReady] = useState(false)
  const [isStateRestored, setIsStateRestored] = useState<boolean>(false)
  const restoreTypes = useMemo<RestoreType[]>(
    () => ['COLUMN_STATE', 'FILTER_STATE'],
    []
  )
  const projectUuid = useMemo(() => {
    return undefined
  }, [])

  const bulkSheetState = useBulkSheetState(
    functionUuid,
    'ProjectClosingStatus',
    UiStateKey.BulkSheetUIStateColumnAndFilter,
    restoreTypes,
    projectUuid
  )

  const columnSetting = useColumnSetting()
  const dialogState = useDialog()
  const { dialogOpen, openDialog, closeDialog, onExportExcel } = useExcel(
    gridApiRef.current
  )

  const refresh = useCallback(async () => {
    if (!pageState.initialized) return
    const start = new DateVO(startDate).formatForApi()
    const end = new DateVO(endDate).formatForApi()
    await fetchRecords(start!, end!, searchCondition)
  }, [pageState.initialized, startDate, endDate, fetchRecords, searchCondition])

  const fetchProjectCount = useCallback(async () => {
    if (!pageState.initialized) return
    const date = new DateVO(startDate).formatForApi()!
    const count = await fetchProjectClosingStatusCount(date, searchCondition)
    setProjectCount(count.json as number)
  }, [pageState.initialized, searchCondition, setProjectCount, startDate])

  useEffect(() => {
    if (!pageState.initialized) return
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, pageState.initialized])

  useEffect(() => {
    if (!pageState.initialized) return
    fetchProjectCount()
  }, [fetchProjectCount, pageState.initialized])

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filterState = e.api.getFilterModel()
      bulkSheetState.saveFilterState(filterState)
      setFilteredColumns(
        Object.keys(filterState)
          .map(col => e.api.getColumnDef(col))
          .filter(v => !!v)
      )
    },
    [bulkSheetState]
  )

  const onDeleteFilteredColumn = useCallback(
    (column: ColDef) => {
      if (!column || !gridApiRef.current) return
      const filterModel = gridApiRef.current.getFilterModel()
      const colKey = column.colId || column.field || ''
      delete filterModel[colKey]
      gridApiRef.current.setFilterModel(filterModel)

      setFilteredColumns(prev =>
        prev.filter(col => (col.colId || col.field || '') !== colKey)
      )
      bulkSheetState.saveFilterState(filterModel)
    },
    [bulkSheetState]
  )

  const resetFilters = useCallback(() => {
    if (!gridApiRef.current) return
    gridApiRef.current.setFilterModel(null)

    setFilteredColumns([])
    bulkSheetState.saveFilterState({})
  }, [bulkSheetState])

  const rememberColumnState = useCallback(() => {
    const columnState = gridApiRef.current?.getColumnState()
    columnState && bulkSheetState.saveColumnState(columnState)
  }, [bulkSheetState])

  const onColumnResized = useCallback(() => {
    rememberColumnState()
  }, [rememberColumnState])

  const onColumnMoved = useCallback(() => {
    rememberColumnState()
  }, [rememberColumnState])

  const onSortedColumnsStateChanged = useCallback(
    (e: SortChangedEvent) => {
      const sortedColumns = e.api
        .getColumnState()
        .filter(col => !!col.sort)
        .map(col => {
          return {
            ...e.api.getColumnDef(col.colId),
            colId: col.colId,
          }
        })
        .filter(v => !!v) as ColDef[]

      const columnState = gridApiRef.current?.getColumnState()
      const sortedColumnState: { [colId: string]: ColumnState } = {}
      columnState &&
        columnState.forEach(state => {
          if (state.sort) {
            sortedColumnState[state.colId] = state
          }
        })

      const sortedColumnsState: SortedColumnState[] = sortedColumns.map(col => {
        return {
          colId: col.colId,
          field: col.field,
          headerName: col.headerName,
          sort: col.colId ? sortedColumnState[col.colId]?.sort : null,
        }
      })

      setSortColumnsState(sortedColumnsState)
      bulkSheetState.saveColumnState(columnState || [])
    },
    [bulkSheetState]
  )

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      if (e.api && e.api) {
        rememberColumnState()
        onSortedColumnsStateChanged(e)
      }
    },
    [rememberColumnState, onSortedColumnsStateChanged]
  )

  const onDeleteSortedColumn = useCallback(
    (colId: string | ColDef<any>) => {
      if (!gridApiRef.current) return
      const colIdStr = colId.toString()
      gridApiRef.current.applyColumnState({
        state: [{ colId: colIdStr, sort: null }],
      })

      setSortColumnsState(prev => prev.filter(col => col.colId !== colIdStr))

      const columnState = gridApiRef.current.getColumnState()
      bulkSheetState.saveColumnState(columnState)
    },
    [bulkSheetState]
  )

  const onDeleteSortedAllColumns = useCallback(() => {
    if (!gridApiRef.current) return
    gridApiRef.current.applyColumnState({
      defaultState: { sort: null },
    })

    setSortColumnsState([])

    const columnState = gridApiRef.current.getColumnState()
    bulkSheetState.saveColumnState(columnState)
  }, [bulkSheetState])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef<any>, sort: 'asc' | 'desc' | null) => {
      if (!gridApiRef.current) return
      gridApiRef.current.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    []
  )

  const onGridReady = useCallback(
    params => {
      gridApiRef.current = params.api
      setIsGridReady(true)
      setIsStateRestored(true)
    },
    [gridOptions]
  )

  useEffect(() => {
    if (
      isGridReady &&
      bulkSheetState.restored &&
      !_.isEmpty(bulkSheetState.columnState)
    ) {
      gridApiRef.current?.applyColumnState({
        state: bulkSheetState.columnState,
        applyOrder: true,
      })
    }
  }, [isGridReady, bulkSheetState.restored, bulkSheetState.columnState])

  useEffect(() => {
    if (data.length > 0 && gridApiRef.current) {
      setTimeout(() => {
        gridApiRef.current?.refreshCells({
          columns: ['rowNumber'],
          force: true,
        })
      }, 100)
    }
  }, [data])

  const onFirstDataRendered = useCallback(() => {
    if (!_.isEmpty(bulkSheetState.filterState)) {
      gridApiRef.current?.setFilterModel(bulkSheetState.filterState ?? null)
    }
    bulkSheetState.finishRestoring()
  }, [bulkSheetState])

  const getCurrentUIState = useCallback(() => {
    return {
      columnState: bulkSheetState.columnState,
      filterState: bulkSheetState.filterState,
    }
  }, [bulkSheetState.columnState, bulkSheetState.filterState])

  const applyUIState = useCallback(
    (uiState: any) => {
      if (uiState.columnState) {
        bulkSheetState.saveColumnState(uiState.columnState)
        if (!_.isEmpty(uiState.columnState) && gridApiRef.current) {
          gridApiRef.current.applyColumnState({
            state: uiState.columnState,
            applyOrder: true,
          })
        }
      }
      if (uiState.filterState) {
        bulkSheetState.saveFilterState(uiState.filterState)
        if (!_.isEmpty(uiState.filterState) && gridApiRef.current) {
          gridApiRef.current.setFilterModel(uiState.filterState ?? null)
        }
      }
      if (uiState.filterState && Object.keys(uiState.filterState).length > 0) {
        const filterKeys = Object.keys(uiState.filterState)
        const filtered =
          (gridOptions.columnDefs as ColDef[])?.filter(col =>
            filterKeys.includes(col.colId || col.field || '')
          ) || []
        setFilteredColumns(filtered)
      } else {
        setFilteredColumns([])
      }

      if (uiState.columnState && uiState.columnState.length > 0) {
        const sortedColumnsState: SortedColumnState[] = uiState.columnState
          .filter(col => col.sort)
          .map(col => ({
            colId: col.colId,
            sort: col.sort!,
          }))
        setSortColumnsState(sortedColumnsState)
      } else {
        setSortColumnsState([])
      }
    },
    [bulkSheetState, gridOptions.columnDefs]
  )

  useEffect(() => {
    if (
      bulkSheetState.initialized &&
      bulkSheetState.restored &&
      !isStateRestored
    ) {
      if (
        bulkSheetState.filterState &&
        Object.keys(bulkSheetState.filterState).length > 0
      ) {
        const filterKeys = Object.keys(bulkSheetState.filterState)
        const filtered =
          (gridOptions.columnDefs as ColDef[])?.filter(col =>
            filterKeys.includes(col.colId || col.field || '')
          ) || []
        setFilteredColumns(filtered)
      }

      if (bulkSheetState.columnState.length > 0) {
        const sortedColumnsState: SortedColumnState[] =
          bulkSheetState.columnState
            .filter(col => col.sort)
            .map(col => ({
              colId: col.colId,
              sort: col.sort!,
            }))
        setSortColumnsState(sortedColumnsState)
      }
    }
  }, [
    bulkSheetState.initialized,
    bulkSheetState.restored,
    bulkSheetState.filterState,
    bulkSheetState.columnState,
    gridOptions.columnDefs,
    isStateRestored,
  ])

  useEffect(() => {
    gridApiRef.current?.resetRowHeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.rowHeight])

  return (
    <PageArea>
      <ProjectClosingStatusHeader
        loading={!pageState.initialized}
        refresh={refresh}
        onMonthChange={pageState.handleMonthChange}
        currentYear={new DateVO(startDate).getYear()}
        currentMonth={new DateVO(startDate).getMonth()}
        searchCondition={searchCondition}
        onSearchConditionChange={onSearchConditionChange}
        onSearch={refresh}
        projectCount={projectCount}
        onClickExport={openDialog}
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={onDeleteFilteredColumn}
        resetFilters={resetFilters}
        sortColumnsState={sortColumnsState}
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
        onClickColumnSettingButton={columnSetting.toggle}
        columnSettingOpen={columnSetting.isOpen}
        rowHeight={pageState.rowHeight}
        onChangeRowHeight={(value: number) => {
          pageState.setRowHeight(value)
        }}
      />
      <BulkSheetView
        ref={ref}
        rowData={data}
        {...gridOptions}
        onGridReady={onGridReady}
        onFirstDataRendered={onFirstDataRendered}
        onFilterChanged={onFilterChanged}
        onSortChanged={onSortChanged}
        onColumnVisible={rememberColumnState}
        onColumnResized={onColumnResized}
        onColumnMoved={onColumnMoved}
        rowHeight={pageState.rowHeight}
      />
      <ColumnSettingPopper
        open={columnSetting.isOpen}
        anchorEl={columnSetting.anchorEl}
        close={columnSetting.close}
        gridApi={gridApiRef.current}
        height={ref.current?.offsetHeight}
        openSavedUiStateDialog={dialogState.openUiState}
        initializeColumnState={() => {
          gridApiRef.current?.resetColumnState()
          gridApiRef.current?.setFilterModel(null)
          bulkSheetState.saveColumnState([])
          bulkSheetState.saveFilterState({})
          setFilteredColumns([])
          setSortColumnsState([])
        }}
        applicationFunctionUuid={props.uuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={bulkSheetState.columnState}
        offset={90}
      />
      <SavedUIStateDialog
        open={dialogState.uiState}
        onClose={dialogState.closeUiState}
        onSelect={selectedUIState => {
          const uiStateToApply = selectedUIState.UIState || selectedUIState
          applyUIState(uiStateToApply)
          dialogState.closeUiState()
        }}
        applicationFunctionUuid={props.uuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        title={intl.formatMessage({
          id: 'bulksheet.toolbar.columnQuickFilter.label.restore',
        })}
        sharable={false}
        currentUIState={getCurrentUIState()}
      />
      {dialogOpen && (
        <MultiSelectDialog
          onClose={closeDialog}
          onSubmit={(allList, selectedItem) => {
            const selectedColIds = selectedItem.map(col => col.value)
            onExportExcel(selectedColIds)
          }}
          dialogTitle={intl.formatMessage({
            id: 'dialog.exceloutput.columnselect.title',
          })}
          submitButtonTitle={intl.formatMessage({
            id: 'dialog.exceloutput.submit',
          })}
          allCheckBoxLabel={intl.formatMessage({
            id: 'dialog.exceloutput.columnselect.allcheckboxlabel',
          })}
          getSelectList={() => {
            return (gridApiRef.current?.getColumns() || [])
              .filter(
                column =>
                  !['uuid', 'drag', 'action', 'select', 'rowNumber'].includes(
                    column.getColId()
                  )
              )
              .map(column => {
                return {
                  value: column.getColId(),
                  displayName:
                    gridApiRef.current?.getDisplayNameForColumn(column, null) ??
                    '',
                  defaultChecked:
                    column.isVisible() ||
                    column.getColDef().cellRendererParams?.uiMeta?.tree,
                }
              })
          }}
          hideIcon={true}
        />
      )}
    </PageArea>
  )
}

export default pageComponent(ProjectClosingStatus)
